import { useState } from "react";
import { askToConfirm } from "./confirm";
import type { GeneralNote, ShareGeneralNote } from "../api/types";
import { NOTE_TAGS, notesAsText, useGeneralNotes } from "../lib/generalNotes";
import { Icon } from "./icons";
import { toast } from "./toast";

const stem = (f: string | null) => (f ? f.replace(/\.[^/.]+$/, "") : null);

// Tag picker: the suggested tags as toggle chips — click the active one again
// to clear it (a tag is optional).
function TagChips({ value, onChange }: { value: string; onChange: (t: string) => void }) {
  return (
    <div className="gn-tags" role="group" aria-label="Tag (optional)">
      {NOTE_TAGS.map((t) => (
        <button
          key={t}
          type="button"
          className={`chip${value === t ? " on" : ""}`}
          aria-pressed={value === t}
          onClick={() => onChange(value === t ? "" : t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function NoteRow({
  note, onSave, onDelete,
}: {
  note: GeneralNote;
  onSave: (text: string, tag: string) => Promise<unknown>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [tag, setTag] = useState(note.tag);
  const [saving, setSaving] = useState(false);

  if (editing) {
    const save = async () => {
      if (!text.trim()) return;
      setSaving(true);
      try {
        await onSave(text, tag);
        setEditing(false);
      } catch (e) {
        toast(`Couldn't save the note — ${(e as Error).message}`, "error");
      } finally {
        setSaving(false);
      }
    };
    return (
      <li className="gn-item editing">
        <textarea
          className="input gn-text"
          rows={3}
          value={text}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); }}
        />
        <TagChips value={tag} onChange={setTag} />
        <div className="gn-actions">
          <button className="btn ghost sm" onClick={() => { setText(note.text); setTag(note.tag); setEditing(false); }}>
            Cancel
          </button>
          <button className="btn sm" disabled={!text.trim() || saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className="gn-item">
      <div className="gn-meta">
        {note.tag && <span className="gn-tag">{note.tag}</span>}
        {note.audio_filename && <span className="gn-from" title="Written while reviewing this file">from {stem(note.shown_filename || note.audio_filename)}</span>}
        <span className="gn-item-actions">
          <button className="btn ghost sm" title="Edit note" onClick={() => setEditing(true)}>
            <Icon name="edit" size={14} />
          </button>
          <button
            className="btn ghost sm"
            title="Delete note"
            onClick={async () => { if (await askToConfirm("Delete this general note?", { yes: "Delete", danger: true })) onDelete(); }}
          >
            <Icon name="trash" size={14} />
          </button>
        </span>
      </div>
      <p className="gn-body">{note.text}</p>
    </li>
  );
}

/**
 * General notes panel for the report drawer: production-wide notes (mastering,
 * EQ, noise, performance…) that aren't tied to a moment in one chapter. The same
 * list shows in every chapter; a new note records the file it was written from.
 */
export function GeneralNotesPanel({ projectId, chapterId }: { projectId: string; chapterId: string | undefined }) {
  const notes = useGeneralNotes(projectId);
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");

  const add = async () => {
    if (!text.trim() || notes.adding) return;
    try {
      await notes.add({ text, tag, chapter_id: chapterId ?? null });
      setText("");
      setTag("");
    } catch (e) {
      toast(`Couldn't add the note — ${(e as Error).message}`, "error");
    }
  };

  return (
    <div className="gn-panel">
      <div className="gn-head">
        <h3>General notes</h3>
        <p className="muted">
          For issues across the whole production — mastering, EQ, noise, performance. They're listed at the top of
          the Pickups Package.
        </p>
      </div>
      <div className="gn-new">
        <textarea
          className="input gn-text"
          rows={3}
          placeholder="e.g. The whole book sounds over-compressed"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
        />
        <TagChips value={tag} onChange={setTag} />
        <div className="gn-actions">
          <button className="btn sm" disabled={!text.trim() || notes.adding} onClick={add}>
            {notes.adding ? "Adding…" : "Add note"}
          </button>
        </div>
      </div>
      {notes.isLoading ? (
        <p className="muted gn-empty">Loading…</p>
      ) : notes.list.length === 0 ? (
        <p className="muted gn-empty">No general notes yet.</p>
      ) : (
        <ul className="gn-list">
          {[...notes.list].reverse().map((n) => (
            <NoteRow
              key={n.id}
              note={n}
              onSave={(t, g) => notes.update({ id: n.id, text: t, tag: g })}
              onDelete={() => notes.remove(n.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Read-only list for the top of the Pickups Package (owner + shared view). */
export function GeneralNotesList({ notes }: { notes: ShareGeneralNote[] }) {
  if (notes.length === 0) return null;
  return (
    <section className="gn-package" aria-label="General notes">
      <div className="gn-package-head">
        <Icon name="notebook" size={16} /> General notes
        <span className="muted">· {notes.length}</span>
        <button
          className="btn ghost sm gn-copy"
          title="Copy the notes, grouped by tag"
          onClick={() =>
            navigator.clipboard.writeText(notesAsText(notes)).then(
              () => toast("General notes copied.", "success"),
              () => toast("Couldn't reach the clipboard — select the notes and copy.", "error"),
            )
          }
        >
          <Icon name="copy" size={14} /> Copy notes
        </button>
      </div>
      <ul className="gn-list">
        {notes.map((n, i) => (
          <li key={i} className="gn-item">
            {(n.tag || n.audio_filename) && (
              <div className="gn-meta">
                {n.tag && <span className="gn-tag">{n.tag}</span>}
                {n.audio_filename && <span className="gn-from">from {stem(n.audio_filename)}</span>}
              </div>
            )}
            <p className="gn-body">{n.text}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
